import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import RTFilters from '../common/RTFilters';
import { apiGet } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../common/rtCommon.css';
import './ExpenseReport.css';
import RTPageLayout from '../common/RTPageLayout';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const CHART_COLORS = [
  '#153C7F',
  '#2563eb',
  '#0d9488',
  '#d97706',
  '#6366f1',
  '#dc2626',
  '#0891b2',
  '#64748b',
];

const formatCurrency = (amount) =>
  `₹${parseFloat(amount || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const formatCurrencyExact = (amount) =>
  `₹${parseFloat(amount || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getDefaultFilters = (period = 'monthly') => ({
  month: new Date().getMonth() + 1,
  year: new Date().getFullYear(),
  period,
});

const ExpenseReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [period, setPeriod] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState(() => getDefaultFilters('monthly'));
  const [appliedFilters, setAppliedFilters] = useState(() =>
    getDefaultFilters('monthly'),
  );

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear - 5; year <= currentYear + 1; year++) {
      years.push({ value: year, label: year.toString() });
    }
    return years;
  }, []);

  const filterFields = useMemo(() => {
    const fields = [];
    if (period === 'monthly') {
      fields.push({
        key: 'month',
        label: 'Month',
        type: 'dropdown',
        width: 'compact',
        options: MONTH_OPTIONS,
      });
    }
    fields.push({
      key: 'year',
      label: 'Year',
      type: 'dropdown',
      width: 'compact',
      options: yearOptions,
    });
    return fields;
  }, [period, yearOptions]);

  const loadReport = async (
    filterParams = appliedFilters,
    periodKey = period,
  ) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('period', periodKey);
      params.append('year', filterParams.year);
      if (periodKey === 'monthly') {
        params.append('month', filterParams.month);
      }

      const response = await apiGet(`/reports/expenses/?${params.toString()}`);
      if (response.data && response.data.success) {
        setReportData(response.data.data);
        toastSuccess('Expense report loaded');
      } else {
        toastError('Failed to load expense report');
      }
    } catch (error) {
      console.error('Error loading expense report:', error);
      toastError('Error loading expense report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport(appliedFilters, period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePeriodChange = (nextPeriod) => {
    if (nextPeriod === period) return;
    const nextFilters = {
      ...filters,
      period: nextPeriod,
      month: filters.month || new Date().getMonth() + 1,
      year: filters.year || new Date().getFullYear(),
    };
    setPeriod(nextPeriod);
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    loadReport(nextFilters, nextPeriod);
  };

  const handleFilterChange = (key, value, { applyImmediately } = {}) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    if (applyImmediately) {
      setAppliedFilters(next);
      loadReport(next, period);
    }
  };

  const handleClearFilters = () => {
    const defaults = getDefaultFilters(period);
    setFilters(defaults);
    setAppliedFilters(defaults);
    loadReport(defaults, period);
  };

  const appliedFilterPills = useMemo(() => {
    const pills = [];
    if (period === 'monthly') {
      const monthLabel = MONTH_OPTIONS.find(
        (o) => o.value === appliedFilters.month,
      )?.label;
      if (monthLabel)
        pills.push({ key: 'month', label: `Month: ${monthLabel}` });
    }
    if (appliedFilters.year) {
      pills.push({ key: 'year', label: `Year: ${appliedFilters.year}` });
    }
    return pills;
  }, [appliedFilters, period]);

  const handleRemoveAppliedFilter = (key) => {
    const next = { ...appliedFilters };
    if (key === 'month') next.month = new Date().getMonth() + 1;
    else if (key === 'year') next.year = new Date().getFullYear();
    setFilters(next);
    setAppliedFilters(next);
    loadReport(next, period);
  };

  const summary = reportData?.summary || {};
  const topCategories = reportData?.top_categories || [];
  const byCategory = reportData?.by_category || [];
  const byStaff = reportData?.by_staff || [];
  const byPaymentMethod = reportData?.by_payment_method || [];
  const trend = reportData?.trend || [];

  const staffVsOther = useMemo(() => {
    const staffAmt = summary.staff_expense_amount || 0;
    const otherAmt = summary.non_staff_amount || 0;
    if (staffAmt <= 0 && otherAmt <= 0) return [];
    return [
      { name: 'Staff expenses', value: staffAmt },
      { name: 'Other expenses', value: otherAmt },
    ].filter((d) => d.value > 0);
  }, [summary]);

  if (loading && !reportData) {
    return (
      <RTPageLayout activePath={location.pathname}>
        <div className="text-center mt-5">
          <RTSpinner message="Loading expense report..." />
        </div>
      </RTPageLayout>
    );
  }

  return (
    <RTPageLayout activePath={location.pathname} title="Expense Report">
      <div className="expense-report-tabs">
        <button
          type="button"
          className={`expense-report-tab${period === 'monthly' ? ' active' : ''}`}
          onClick={() => handlePeriodChange('monthly')}
        >
          Monthly
        </button>
        <button
          type="button"
          className={`expense-report-tab${period === 'yearly' ? ' active' : ''}`}
          onClick={() => handlePeriodChange('yearly')}
        >
          Yearly
        </button>
      </div>

      <div className="rt-section" style={{ flex: 'none' }}>
        <RTFilters
          fields={filterFields}
          values={filters}
          onChange={handleFilterChange}
          pills={appliedFilterPills}
          onRemovePill={handleRemoveAppliedFilter}
          onClearAll={handleClearFilters}
        />
      </div>

      {loading && reportData && (
        <div className="text-center mb-3">
          <RTSpinner message="Loading expense report..." />
        </div>
      )}

      {reportData && (
        <div className="expense-report-content">
          <div className="expense-period-label">
            Showing: <strong>{reportData.period_label}</strong>
          </div>

          <div className="expense-stat-boxes">
            <div className="expense-stat-box">
              <div className="expense-stat-label">Total Expenses</div>
              <div className="expense-stat-count">
                {summary.total_count || 0}
              </div>
              <div className="expense-stat-amount">
                {formatCurrencyExact(summary.total_amount)}
              </div>
            </div>
            <div className="expense-stat-box accent-teal">
              <div className="expense-stat-label">Average Expense</div>
              <div className="expense-stat-amount large">
                {formatCurrencyExact(summary.avg_amount)}
              </div>
              <div className="expense-stat-sub">Per transaction</div>
            </div>
            <div className="expense-stat-box accent-amber">
              <div className="expense-stat-label">Staff Expenses</div>
              <div className="expense-stat-amount">
                {formatCurrencyExact(summary.staff_expense_amount)}
              </div>
              <div className="expense-stat-sub">Salary / staff categories</div>
            </div>
            <div className="expense-stat-box accent-green">
              <div className="expense-stat-label">Other Expenses</div>
              <div className="expense-stat-amount">
                {formatCurrencyExact(summary.non_staff_amount)}
              </div>
              <div className="expense-stat-sub">Non-staff categories</div>
            </div>
          </div>

          <div className="expense-section">
            <h3>Top 3 Expense Categories</h3>
            {topCategories.length > 0 ? (
              <div className="expense-top-grid">
                {topCategories.map((cat, index) => (
                  <div
                    key={cat.name}
                    className={`expense-top-card rank-${index + 1}`}
                  >
                    <div className="expense-top-rank">#{index + 1}</div>
                    <div className="expense-top-name">{cat.name}</div>
                    <div className="expense-top-amount">
                      {formatCurrencyExact(cat.amount)}
                    </div>
                    <div className="expense-top-count">
                      {cat.count} transaction{cat.count !== 1 ? 's' : ''}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="expense-no-data">
                No category data for this period
              </div>
            )}
          </div>

          <div className="expense-charts-row">
            <div className="expense-section">
              <h3>Expenses by Category</h3>
              {byCategory.length > 0 ? (
                <div className="expense-chart-wrap">
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={byCategory}
                        dataKey="amount"
                        nameKey="name"
                        cx="40%"
                        cy="50%"
                        outerRadius={95}
                        innerRadius={45}
                        paddingAngle={2}
                      >
                        {byCategory.map((_, index) => (
                          <Cell
                            key={index}
                            fill={CHART_COLORS[index % CHART_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name) => [
                          formatCurrencyExact(value),
                          name,
                        ]}
                      />
                      <Legend
                        layout="vertical"
                        align="right"
                        verticalAlign="middle"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="expense-no-data">No category breakdown</div>
              )}
            </div>

            <div className="expense-section">
              <h3>Staff vs Other</h3>
              {staffVsOther.length > 0 ? (
                <div className="expense-chart-wrap">
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={staffVsOther}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={95}
                        label={({ name, percent }) =>
                          `${name} (${(percent * 100).toFixed(0)}%)`
                        }
                      >
                        {staffVsOther.map((_, index) => (
                          <Cell
                            key={index}
                            fill={index === 0 ? '#d97706' : '#0d9488'}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => formatCurrencyExact(value)}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="expense-no-data">No staff/other split</div>
              )}
            </div>
          </div>

          <div className="expense-section">
            <h3>
              {period === 'monthly'
                ? 'Daily Expense Trend'
                : 'Monthly Expense Trend'}
            </h3>
            {trend.some((t) => t.amount > 0) ? (
              <div className="expense-chart-wrap">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={trend}
                    margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                    <YAxis
                      tickFormatter={(v) => formatCurrency(v)}
                      tick={{ fontSize: 12 }}
                    />
                    <Tooltip
                      formatter={(value, name) =>
                        name === 'amount'
                          ? [formatCurrencyExact(value), 'Amount']
                          : [value, 'Count']
                      }
                    />
                    <Bar
                      dataKey="amount"
                      fill="#153C7F"
                      radius={[4, 4, 0, 0]}
                      name="amount"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="expense-no-data">
                No trend data for this period
              </div>
            )}
          </div>

          <div className="expense-charts-row">
            <div className="expense-section">
              <h3>Expenses by Staff</h3>
              {byStaff.length > 0 ? (
                <div className="expense-chart-wrap">
                  <ResponsiveContainer
                    width="100%"
                    height={Math.max(240, byStaff.length * 42)}
                  >
                    <BarChart
                      data={byStaff}
                      layout="vertical"
                      margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        type="number"
                        tickFormatter={(v) => formatCurrency(v)}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={110}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value) => formatCurrencyExact(value)}
                      />
                      <Bar
                        dataKey="amount"
                        fill="#d97706"
                        radius={[0, 4, 4, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="expense-no-data">
                  No staff expenses in this period
                </div>
              )}
            </div>

            <div className="expense-section">
              <h3>Payment Methods</h3>
              {byPaymentMethod.length > 0 ? (
                <div className="expense-chart-wrap">
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={byPaymentMethod}
                      margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="method" tick={{ fontSize: 12 }} />
                      <YAxis
                        tickFormatter={(v) => formatCurrency(v)}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value) => formatCurrencyExact(value)}
                      />
                      <Bar
                        dataKey="amount"
                        fill="#2563eb"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="expense-no-data">No payment method data</div>
              )}
            </div>
          </div>
        </div>
      )}
    </RTPageLayout>
  );
};

export default ExpenseReport;
